import { useEffect, useState } from 'react';
// @ts-ignore - Prisma client types
import type {  User  } from '@prisma/client';
import Image from 'next/image';
import { debounce } from 'lodash';

interface MentionDropdownProps {
  searchText: string;
  onSelect: (user: User) => void;
  onClose: () => void;
  position: { top: number; left: number };
}

export default function MentionDropdown({
  searchText,
  onSelect,
  onClose,
  position,
}: MentionDropdownProps) {
  const [users, setUsers] = useState<Partial<User>[]>([]);
  const [loading, setLoading] = useState(false);

  const searchUsers = debounce(async (query: string) => {
    if (query.length < 1) {
      setUsers([]);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${process?.env?.NEXT_PUBLIC_API_URL}/users/search?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setLoading(false);
    }
  }, 300);

  useEffect(() => {
    searchUsers(searchText);
    return () => searchUsers.cancel();
  }, [searchText]);

  if (!searchText) return null;

  return (
    <div
      className="absolute z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto w-64"
      style={{ top: position.top, left: position.left }}
    >
      {loading ? (
        <div className="p-4 text-center text-gray-500">Loading...</div>
      ) : users.length === 0 ? (
        <div className="p-4 text-center text-gray-500">No users found</div>
      ) : (
        <ul className="py-2">
          {users?.map((user) => (
            <li
              key={user.id}
              className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2"
              onClick={() => {
                onSelect(user as User);
                onClose();
              }}
            >
              {user.image && (
                <Image
                  src={user.image || "/default-avatar.png"}
                  alt={user.name || ''}
                  width={24}
                  height={24}
                  className="rounded-full"
                />
              )}
              <div>
                <div className="font-medium">{user.name}</div>
                <div className="text-sm text-gray-500">@{user.username}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}